import {createSlice, isAnyOf} from '@reduxjs/toolkit';

import {
    getInitialFacilities,
    setFilteredFacilities,
    getFacilityDetailsData,
    updateSelectedFacilityId,
    getFilterOptionsData,
    updateAppliedFilters,
    clearAppliedFilters,
    getPaginatedFacilitiesData,
    getSearchedFacilitiesData,
    setFacilitySearchText,
    ERRORS,
} from './facilities-actions';
import {
    getFacilityMicroclimateMetaData,
    getFacilityMicroclimateSnapshotData,
} from './microclimate-actions';
import {getStarRatingsData} from './star-ratings-actions';
import {
    getReviewTagsData,
    setReviewTagsPeriod,
    setReviewTagsReservationTypes,
} from './review-tags-actions';
import {
    facilitiesAdapter,
    filteredFacilitiesAdapter,
} from './facilities-adapters';

const initialState = {
    canonicalFacilities: facilitiesAdapter.getInitialState({
        isLoading: false,
        error: null,
    }),
    facilities: facilitiesAdapter.getInitialState({
        isLoading: false,
        error: null,
    }),
    meta: {
        limit: 25,
        offset: 0,
        nextOffset: 25,
        total: 25,
        totalFacilityCount: 25,
        totalFilteredFacilityCount: 25,
    },
    filteredFacilities: filteredFacilitiesAdapter.getInitialState({
        isLoading: false,
    }),
    selectedFacilityId: null,
    isLoading: false,
    error: null,
    searchFilters: {
        cities: [],
        status: [],
    },
    appliedFilters: {
        cities: [],
        status: [],
        operatorEmail: '',
        searchText: '',
    },
    starRatings: {
        aggregatedRatings: [],
        cityRatings: [],
        start: null,
        end: null,
        facilityId: null,
        microclimateRatings: [],
    },
    reviewTags: {
        facilityId: 0,
        totals: {
            positive: 0,
            performancePercentage: 0,
            negative: 0,
        },
        tags: [
            {
                emoji: '',
                count: 0,
                performancePercentage: 0,
                isPositive: true,
                percentOfTotal: 0,
                label: '',
            },
        ],
        hasMicroclimateData: false,
        filters: {
            period: '30D',
            reservationTypes: [],
        },
    },
};

const facilitiesSlice = createSlice({
    name: 'facilities',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(getReviewTagsData.fulfilled, (state, {payload}) => {
                state.reviewTags = {
                    ...state.reviewTags,
                    ...payload,
                    filters: state.reviewTags.filters,
                };
            })
            .addCase(setReviewTagsPeriod, (state, {payload}) => {
                state.reviewTags.filters.period = payload;
            })
            .addCase(setReviewTagsReservationTypes, (state, {payload}) => {
                state.reviewTags.filters.reservationTypes = payload;
            })
            .addCase(getStarRatingsData.fulfilled, (state, {payload}) => {
                state.starRatings = payload;
            })
            .addCase(setFacilitySearchText, (state, {payload}) => {
                state.appliedFilters.searchText = payload;
            })
            .addCase(getFilterOptionsData.fulfilled, (state, {payload}) => {
                state.searchFilters = payload;
            })
            .addCase(updateAppliedFilters, (state, {payload}) => {
                state.appliedFilters = payload;
            })
            .addCase(clearAppliedFilters, (state) => {
                state.appliedFilters = {
                    ...initialState.appliedFilters,
                    status: [],
                };
            })
            .addCase(getPaginatedFacilitiesData.fulfilled, (state, action) => {
                const {payload} = action;
                const {canonicalFacilities, parkingSpots} = payload?.entities;

                facilitiesAdapter.upsertMany(
                    state.canonicalFacilities,
                    canonicalFacilities
                );

                facilitiesAdapter.upsertMany(state.facilities, parkingSpots);

                state.meta = payload.meta;
                state.facilities.isLoading = false;
                state.appliedFilters = {
                    ...state.appliedFilters,
                    ...action.meta.arg.filters,
                };
            })
            .addCase(getPaginatedFacilitiesData.pending, (state) => {
                state.facilities.isLoading = true;
            })
            .addCase(
                getSearchedFacilitiesData.fulfilled,
                (state, {payload}) => {
                    const {canonicalFacilities, parkingSpots} =
                        payload?.entities;

                    facilitiesAdapter.setAll(
                        state.canonicalFacilities,
                        canonicalFacilities
                    );

                    facilitiesAdapter.setAll(state.facilities, parkingSpots);

                    state.meta = payload.meta;
                    state.isLoading = false;
                    state.error = false;
                }
            )
            .addCase(getSearchedFacilitiesData.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(getSearchedFacilitiesData.rejected, (state, {payload}) => {
                state.isLoading = false;
                state.error = payload.message;

                if (payload.message === ERRORS.NO_RESULTS_FOUND) {
                    state.meta = payload.meta;
                }
            })
            .addCase(getInitialFacilities.fulfilled, (state) => {
                state.isLoading = false;
            })
            .addCase(setFilteredFacilities.pending, (state) => {
                state.filteredFacilities.isLoading = true;
            })
            .addCase(setFilteredFacilities.fulfilled, (state, {payload}) => {
                filteredFacilitiesAdapter.setAll(
                    state.filteredFacilities,
                    payload
                );

                state.filteredFacilities.isLoading = false;
            })
            .addCase(getFacilityDetailsData.fulfilled, (state, {payload}) => {
                facilitiesAdapter.upsertOne(state.facilities, payload);

                filteredFacilitiesAdapter.upsertOne(
                    state.filteredFacilities,
                    payload
                );

                state.isLoading = false;
            })
            .addCase(
                getFacilityMicroclimateMetaData.fulfilled,
                (state, {payload}) => {
                    facilitiesAdapter.upsertOne(state.facilities, {
                        id: payload.facilityId,
                        microclimate: payload.response,
                    });

                    if (
                        state.filteredFacilities.ids.includes(
                            payload.facilityId
                        )
                    ) {
                        filteredFacilitiesAdapter.upsertOne(
                            state.filteredFacilities,
                            {
                                id: payload.facilityId,
                                microclimate: payload.response,
                            }
                        );
                    }

                    state.isLoading = false;
                }
            )
            .addCase(
                getFacilityMicroclimateSnapshotData.fulfilled,
                (state, {payload}) => {
                    facilitiesAdapter.upsertOne(state.facilities, {
                        id: payload.facilityId,
                        microclimateSnapshotData: payload.response,
                    });

                    if (
                        state.filteredFacilities.ids.includes(
                            payload.facilityId
                        )
                    ) {
                        filteredFacilitiesAdapter.upsertOne(
                            state.filteredFacilities,
                            {
                                id: payload.facilityId,
                                microclimateSnapshotData: payload.response,
                            }
                        );
                    }
                }
            )
            .addCase(updateSelectedFacilityId, (state, {payload}) => {
                state.selectedFacilityId = payload;
            })
            .addMatcher(isAnyOf(getInitialFacilities.pending), (state) => {
                return {
                    ...state,
                    isLoading: true,
                    error: false,
                };
            })
            .addMatcher(
                isAnyOf(
                    getInitialFacilities.rejected,
                    getFacilityDetailsData.rejected
                ),
                (state) => {
                    return {
                        ...state,
                        isLoading: false,
                        error: true,
                    };
                }
            );
    },
});

export default facilitiesSlice.reducer;
